import type React from "react";
import type { JourneyPhase } from "../../generated-api";
import { ConfirmationWidget } from "./widgets/ConfirmationWidget";
import { MultipleChoiceWidget } from "./widgets/MultipleChoiceWidget";
import { ProgramPreviewWidget } from "./widgets/ProgramPreviewWidget";
import { AssessmentWidget } from "./widgets/AssessmentWidget";
import { ProfileSaveWidget } from "./widgets/ProfileSaveWidget";
import { TargetsSaveWidget } from "./widgets/TargetsSaveWidget";
import { upsertProfile } from "./actions/upsertProfile";
import {} from "./actions/createProgram";
import { upsertTargets } from "./actions/upsertTargets";
import { patchTarget } from "./actions/patchTarget";
import { ProgramPatchWidget } from "./widgets/ProgramPatchWidget";
import { reviewProgram } from "./actions/reviewProgram";
import { extendProgram } from "./actions/extendProgram";

export type WidgetProps<T = unknown> = {
  payload: T;
  onComplete: (result: unknown) => void;
  journeyPhase?: JourneyPhase | null;
};

export type ActionHandler = (data: unknown) => Promise<unknown>;

export type WidgetRegistryEntry<T = unknown> = {
  kind: string;
  component: React.ComponentType<WidgetProps<T>>;
  mergeAttempt?: (current: unknown, next: unknown) => unknown;
  action?: ActionHandler;
};

export const actionRegistry: Record<string, WidgetRegistryEntry> = {
  confirmation: {
    kind: "confirmation",
    component: ConfirmationWidget as React.ComponentType<WidgetProps<unknown>>,
  },
  multiple_choice: {
    kind: "multiple_choice",
    component: MultipleChoiceWidget as React.ComponentType<
      WidgetProps<unknown>
    >,
  },
  program_preview: {
    kind: "program_preview",
    component: ProgramPreviewWidget as React.ComponentType<
      WidgetProps<unknown>
    >,
    action: async (data) => {},
  },
  program_patch: {
    kind: "program_patch",
    component: ProgramPatchWidget as React.ComponentType<WidgetProps<unknown>>,
    action: async (data) => {},
  },
  program_close: {
    kind: "program_close",
    component: ConfirmationWidget as React.ComponentType<WidgetProps<unknown>>,
    action: reviewProgram,
  },
  program_extend: {
    kind: "program_extend",
    component: ConfirmationWidget as React.ComponentType<WidgetProps<unknown>>,
    action: extendProgram,
  },
  assessment: {
    kind: "assessment",
    component: AssessmentWidget as React.ComponentType<WidgetProps<unknown>>,
  },
  profile_save: {
    kind: "profile_save",
    component: ProfileSaveWidget as React.ComponentType<WidgetProps<unknown>>,
    action: upsertProfile,
  },
  targets_save: {
    kind: "targets_save",
    action: upsertTargets,
    component: TargetsSaveWidget as React.ComponentType<WidgetProps<unknown>>,
  },
  targets_patch: {
    kind: "targets_patch",
    component: ConfirmationWidget as React.ComponentType<WidgetProps<unknown>>,
    action: patchTarget,
  },
};

export const widgetRegistry = actionRegistry;

export const getWidgetEntries = (filter?: string[]) => {
  if (!filter) {
    return Object.values(widgetRegistry);
  }
  return Object.values(widgetRegistry).filter((wr) => filter.includes(wr.kind));
};
